import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from "@/integrations/supabase/client";
import { User, Session } from '@supabase/supabase-js';
import { AuthContextType } from '@/types/auth';
import { useCreateDemoAdmin } from '@/hooks/useCreateDemoAdmin';
import { useUserRole } from '@/hooks/useUserRole';
import { useAuthMethods } from '@/hooks/useAuthMethods';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';

// Create a context to store the redirect path after login
export interface RedirectContext {
  redirectPath: string | null;
  setRedirectPath: (path: string | null) => void;
}

export const RedirectContext = createContext<RedirectContext>({
  redirectPath: null,
  setRedirectPath: () => {}
});

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [initComplete, setInitComplete] = useState(false);
  const [redirectPath, setRedirectPath] = useState<string | null>(null);
  const { toast } = useToast();
  const navigate = useNavigate();
  
  const { createDemoAdminIfNeeded } = useCreateDemoAdmin();
  const { userRole, fetchUserRole, setUserRole } = useUserRole();
  const { signIn, signUp, signInWithGoogle, signOut, googleAuthInProgress } = useAuthMethods(fetchUserRole);

  // This effect handles OAuth redirects and session initialization
  useEffect(() => {
    async function initAuth() {
      setLoading(true);
      
      try {
        await createDemoAdminIfNeeded();
        
        // First set up the auth state change listener
        const { data: { subscription } } = supabase.auth.onAuthStateChange(
          async (event, session) => {
            console.log('Auth state changed:', event, !!session);
            setSession(session);
            setUser(session?.user ?? null);
            
            if (session?.user) {
              console.log("AuthContext: Auth state change with user, fetching role for", session.user.id);
              await fetchUserRole(session.user.id);
              
              // If this is a sign-in or token-refreshed event, show a success toast
              if (event === 'SIGNED_IN') {
                toast({
                  title: "Signed in successfully",
                  description: "Welcome to InstaMG!",
                });
                
                // Navigate to the redirect path or home
                if (redirectPath) {
                  navigate(redirectPath);
                  setRedirectPath(null);
                } else {
                  navigate('/');
                }
              }
            } else {
              setUserRole(null);
            }
            
            setLoading(false);
          }
        );
        
        // Then check for an existing session
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error("Error getting session:", error);
        }
        
        setSession(session);
        setUser(session?.user ?? null);
        
        if (session?.user) {
          console.log("AuthContext: Initial session check found user, fetching role");
          await fetchUserRole(session.user.id);
        }
        
        setLoading(false);
        setInitComplete(true);
        
        return () => {
          subscription.unsubscribe();
        };
      } catch (error) {
        console.error('Error initializing auth:', error);
        setLoading(false);
        setInitComplete(true);
      }
    }

    initAuth();
  }, []);

  // Improved handling for Google auth redirects
  useEffect(() => {
    const handleAuthRedirect = async () => {
      // Check if we're in a redirect flow - look for hash or query parameters that indicate OAuth
      const isOAuthCallback = 
        (window.location.hash && (window.location.hash.includes('access_token') || window.location.hash.includes('error'))) || 
        (window.location.search && (window.location.search.includes('code=') || window.location.search.includes('error=')));
      
      if (isOAuthCallback) {
        console.log("AuthContext: Detected OAuth callback parameters in URL:", {
          hash: window.location.hash,
          search: window.location.search
        });
        setLoading(true);
        
        // Clear hash and search params to prevent re-processing
        if (window.history && window.history.replaceState) {
          window.history.replaceState({}, document.title, window.location.pathname);
        }
        
        // We're in a redirect flow, wait for auth state to update
        try {
          const { data, error } = await supabase.auth.getSession();
          
          if (data.session && !error) {
            console.log("AuthContext: OAuth redirect successful, session found:", !!data.session);
            setUser(data.session.user);
            setSession(data.session);
            await fetchUserRole(data.session.user.id);
            
            // Navigate to the next page
            if (redirectPath) {
              navigate(redirectPath);
              setRedirectPath(null);
            } else {
              navigate('/');
            }
            
            toast({
              title: "Signed in successfully",
              description: "Welcome to InstaMG!",
            });
          } else if (error) {
            console.error("Error during OAuth redirect:", error);
            toast({
              title: "Authentication Error",
              description: error.message || "Failed to complete authentication",
              variant: "destructive",
            });
          } else {
            console.warn("No session found after OAuth redirect");
          }
          
          setLoading(false);
        } catch (error) {
          console.error("Exception during OAuth redirect handling:", error);
          setLoading(false);
          toast({
            title: "Authentication Error",
            description: "Failed to complete authentication process",
            variant: "destructive",
          });
        }
      }
    };
    
    handleAuthRedirect();
  }, []);

  return (
    <AuthContext.Provider value={{ 
      user, 
      session, 
      signIn, 
      signUp,
      signInWithGoogle,
      signOut, 
      loading: loading || !initComplete, 
      userRole,
      googleAuthInProgress 
    }}>
      <RedirectContext.Provider value={{ redirectPath, setRedirectPath }}>
        {children}
      </RedirectContext.Provider>
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const useRedirect = () => {
  const context = useContext(RedirectContext);
  if (context === undefined) {
    throw new Error('useRedirect must be used within a RedirectProvider');
  }
  return context;
};
